import { _ } from "../../../../polyglot/polyglot.js";
import { helpIconRenderer } from "../../../makeRoadmapStepContent.js";
import { pencilIcon } from "../../../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import AccountIntervalsStrategy from "../../AccountIntervalsStrategy/AccountIntervalsStrategy.js";
import regionWidget from "../../../../widgets/regionWidget.js";
import specificationFactory from "../../../../../models/specification/specificationFactory.js";
import TextRepresentationAccountSpecificationVisitor from "../../../../../models/accountSpecification/visitors/TextRepresentationAccountSpecificationVisitor.js";
import WidgetsListWidget from "../../../../widgets/WidgetsListWidget/WidgetsListWidget.js";

/**
 *	Setter to see and edit {@link AccountIntervalSpecification}s.
 *	This is done by rendering a label containing the {@link AccountIntervalSpecification}s
 *	nicely formatted and a link to open a dialog.
 *  The dialog contains a dynamic form that allows the user to enter as
 * 	many as they want account intervals. This is rendered with a {@link WidgetsListWidget}
 *	and a {@link AccountIntervalsStrategy}.
 *
 * @param {string} title - Title of the dialog
 * @param {string} [titleCssClass] - Css class to apply to the title container.
 *   Default value is "title".
 * @param {string} helpText - Text rendered in the tooltip next to the link to
 *   open the dialog
 * @param {string} dataTestName -
 * @param {string} inputName - Label text rendered above the input text fields
 * @param {function} accessor - function that takes no parameter and that is
 *   expected to return the value of the {@link AccountIntervalSpecification}s
 *   formatted as an array of string
 * @param {function} mutator - function called when the save button is clicked.
 *   It is called with the new values that the user entered that have been
 *   parsed by the function "parseInterval" of {@link specificationFactory}
 */
export default class AccountGroupSetterLineWidget extends Widget {
	constructor({
		title,
		titleCssClass = "title",
		helpText,
		dataTestName,
		inputName,
		accessor,
		mutator,
	}) {
		super();

		this._title = title;
		this._titleCssClass = titleCssClass;
		this._helpText = helpText;
		this._dataTestName = dataTestName;
		this._inputName = inputName;
		this._accessor = accessor;
		this._mutator = mutator;
	}

	renderContentOn(html) {
		const contentRegion = regionWidget({
			content: (html) =>
				html.div(
					{ "data-test": "account-group-content" },
					this._toTextualIntervalsArray(this._accessor).join(", "),
				),
		});

		const onClick = () =>
			this._openDialog({
				mutator: (...rest) => {
					this._mutator(...rest);
					contentRegion.update();
				},
			});

		return html.div(
			{
				class: "AccountGroupSetterLineWidget account-group-setter-grid action",
				"data-test": this._dataTestName,
			},
			html.div({ class: this._titleCssClass }, this._title),
			contentRegion,
			html.div(
				{ class: "select-accounts" },
				this._selectAccountLinkRenderer({ onClick }),
				helpIconRenderer({ helpText: this._helpText }),
			),
		);
	}

	_selectAccountLinkRenderer({ onClick }) {
		return (html) =>
			html.a(
				{
					class: "link",
					click: () => onClick(),
					"data-test": "select-accounts",
				},
				html.div(
					{ class: "icon-and-text" },
					pencilIcon,
					_("Select accounts"),
				),
			);
	}

	_openDialog({ mutator }) {
		const values = this._toTextualIntervalsArray(this._accessor);

		const widget = new WidgetsListWidget({
			strategy: new AccountIntervalsStrategy({
				inputCssClass: "account-group-input",
				items: values,
			}),
		});

		this._inform({
			title: this._title,
			body: (html) =>
				html.div(
					html.h4({ class: "modal-subtitle" }, this._inputName),
					widget,
				),
			contentCssClass: "resizable-modal-content",
			okLabel: _("Save changes"),
			callback: () => {
				if (!widget.validate()) {
					return;
				}
				mutator(specificationFactory.parseInterval(values.join(",")));
			},
		});
	}

	_toTextualIntervalsArray(accessor) {
		const specification = accessor();
		if (!specification) {
			return [];
		}

		const textualSpecification =
			new TextRepresentationAccountSpecificationVisitor().visit(
				specification,
			);

		return textualSpecification.split(", ");
	}
}
